import {
  inject,
  makeEnvironmentProviders,
  type EnvironmentProviders,
} from '@angular/core';

import { APP_CONFIG, type AppConfig } from '../config/app-config.model';
import {
  APPLICATION_INSIGHTS_LOADER,
  type ApplicationInsightsLoader,
} from '../realtime/application-insights-realtime-telemetry';
import {
  AUTH_TELEMETRY_SINK,
  AuthTelemetry,
  AuthTelemetrySink,
  ConsoleAuthTelemetrySink,
  type AuthEvent,
} from './auth-telemetry';

// Re-exported so auth telemetry consumers/tests have a single module surface.
export { APPLICATION_INSIGHTS_LOADER, type ApplicationInsightsLoader };

/**
 * Lazily-imported Application Insights sink for auth lifecycle events
 * (R-111): the SDK is never eagerly loaded; when no connection string is
 * configured the console sink is selected instead. Mirrors the AITH-31
 * realtime telemetry sink selection.
 */
export class ApplicationInsightsAuthTelemetrySink extends AuthTelemetrySink {
  private clientPromise: Promise<ApplicationInsightsClient> | null = null;

  constructor(
    private readonly connectionString: string,
    private readonly loader: ApplicationInsightsLoader,
  ) {
    super();
  }

  override async emit(record: AuthEvent): Promise<void> {
    const client = await this.getClient();
    const { event, ...properties } = record;
    client.trackEvent({ name: `AITH.Auth.${event}` }, properties);
  }

  private getClient(): Promise<ApplicationInsightsClient> {
    this.clientPromise ??= this.loader().then((module) => {
      // Tenant-free telemetry: the SDK's default auto-collectors would emit
      // fetch/XHR dependency URLs and raw uncaught-exception details,
      // bypassing the allowlisted AuthEvent path. Disable all three so only
      // the explicit custom events above are sent.
      const client = new module.ApplicationInsights({
        config: {
          connectionString: this.connectionString,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
        },
      });
      client.loadAppInsights();
      return client;
    });
    return this.clientPromise;
  }
}

interface ApplicationInsightsClient {
  loadAppInsights(): void;
  trackEvent(
    event: { readonly name: string },
    properties?: Readonly<Record<string, string | number>>,
  ): void;
}

export function provideAuthTelemetry(): EnvironmentProviders {
  return makeEnvironmentProviders([
    AuthTelemetry,
    {
      provide: AUTH_TELEMETRY_SINK,
      useFactory: () => createAuthTelemetrySink(
        inject(APP_CONFIG),
        inject(APPLICATION_INSIGHTS_LOADER),
      ),
    },
  ]);
}

function createAuthTelemetrySink(
  config: AppConfig,
  loader: ApplicationInsightsLoader,
): AuthTelemetrySink {
  return config.applicationInsightsConnectionString === null
    ? new ConsoleAuthTelemetrySink()
    : new ApplicationInsightsAuthTelemetrySink(
      config.applicationInsightsConnectionString,
      loader,
    );
}
